import { useState, useEffect, useCallback, useRef } from 'react';
import { postsService } from '../services/api/posts';
import { timelineService } from '../services/api/timeline';
import { uploadPostAttachment } from '../services/mediaService';
import { toUiVisibility } from '../utils/visibility';
import {
    MAX_POST_ATTACHMENTS,
    MAX_POST_ATTACHMENT_SIZE_MB,
    ACCEPTED_POST_ATTACHMENT_TYPES,
} from '../constants/posts';
import { resolveMediaUrl } from '../utils/mediaUrl';
import {
    ATTACHMENTS_ONLY_BODY_PLACEHOLDER,
    normalizePostBody,
    resolveAttachmentsOnlyBody,
} from '../utils/postBody';
import {
    addTrackedPostMutationFields,
    removeTrackedPostMutationFields,
} from '../utils/postMutationState';

const isStandardPostTimelineItem = (item) => (
    !item?.timelineItemKind || item.timelineItemKind === 'POST'
);

const revokePreviewUrl = (draft) => {
    if (!draft || draft.source !== 'local') {
        return;
    }
    if (draft.previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        try {
            URL.revokeObjectURL(draft.previewUrl);
        } catch (_) {
            // no-op
        }
    }
};

const cleanupDrafts = (drafts) => {
    if (!Array.isArray(drafts)) {
        return;
    }
    drafts.forEach(revokePreviewUrl);
};

const isSupportedType = (file) => {
    if (!file || !file.type) {
        return false;
    }
    const lower = file.type.toLowerCase();
    return ACCEPTED_POST_ATTACHMENT_TYPES.some((type) => lower === type.toLowerCase());
};

const createAttachmentId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `post-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const createDraftFromFile = (file) => {
    const id = createAttachmentId();
    let previewUrl = '';
    try {
        if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
            previewUrl = URL.createObjectURL(file);
        }
    } catch (_) {
        previewUrl = '';
    }
    return {
        id,
        file,
        previewUrl,
        source: 'local',
    };
};

const createDraftFromAttachment = (attachment) => ({
    id: attachment?.id || attachment?.objectKey || createAttachmentId(),
    previewUrl: resolveMediaUrl(attachment?.url || ''),
    source: 'remote',
    metadata: attachment,
});

const MAX_ERROR_MESSAGE_LENGTH = 160;
const POSTS_LOAD_ERROR_MESSAGE = 'Unable to load posts. Check your connection and try again.';

const sanitizeErrorMessage = (message, defaultMessage) => {
    const safeDefaultMessage = typeof defaultMessage === 'string' && defaultMessage.trim()
        ? defaultMessage
        : 'Something went wrong.';
    if (typeof message !== 'string') {
        return safeDefaultMessage;
    }
    const normalized = message.trim().replace(/\s+/g, ' ').slice(0, MAX_ERROR_MESSAGE_LENGTH);
    if (!normalized) {
        return safeDefaultMessage;
    }
    if (/(https?:\/\/|www\.)/i.test(normalized)) {
        return safeDefaultMessage;
    }
    if (/[?&][^\s]+=[^\s]+/.test(normalized)) {
        return safeDefaultMessage;
    }
    if (/\b(bearer|token|jwt|cookie|authorization|set-cookie|session|password)\b/i.test(normalized)) {
        return safeDefaultMessage;
    }
    if (/[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/.test(normalized)) {
        return safeDefaultMessage;
    }
    if (/\b[A-Za-z0-9_-]{40,}\b/.test(normalized)) {
        return safeDefaultMessage;
    }
    if (!/^[A-Za-z0-9 .,'":;()!?/_-]+$/.test(normalized)) {
        return safeDefaultMessage;
    }
    return normalized;
};

const getSafeErrorMessage = (error, defaultMessage) => (
    sanitizeErrorMessage(error?.response?.data?.message || error?.message, defaultMessage)
);

const DRAFT_RECOVERY_MESSAGE = 'Your post may have been created but not finalized. You can retry or delete it manually.';
const RECOVERABLE_CREATE_FAILURE_STAGES = new Set(['upload', 'update']);

const appendDraftRecoveryMessage = (message) => {
    if (typeof message !== 'string' || !message.trim()) {
        return DRAFT_RECOVERY_MESSAGE;
    }
    if (message.includes(DRAFT_RECOVERY_MESSAGE)) {
        return message;
    }
    const trimmed = message.trim();
    const separator = /[.!?]$/.test(trimmed) ? ' ' : '. ';
    return `${trimmed}${separator}${DRAFT_RECOVERY_MESSAGE}`;
};

const resolveErrorStatus = (error) => {
    if (typeof error?.status === 'number') {
        return error.status;
    }
    if (typeof error?.response?.status === 'number') {
        return error.response.status;
    }
    if (typeof error?.response?.statusCode === 'number') {
        return error.response.statusCode;
    }
    return null;
};

const isClientErrorStatus = (status) => typeof status === 'number' && status >= 400 && status < 500;

const isRecoverableCreateFailureStage = (stage) => RECOVERABLE_CREATE_FAILURE_STAGES.has(stage);

const getAttachmentDraftSignature = (drafts) => {
    if (!Array.isArray(drafts) || drafts.length === 0) {
        return '[]';
    }
    return JSON.stringify(drafts.map((draft) => ({
        id: draft?.id || '',
        source: draft?.source || '',
        remoteId: draft?.metadata?.id || draft?.metadata?.objectKey || '',
        fileName: draft?.file?.name || '',
        fileSize: draft?.file?.size || 0,
        fileType: draft?.file?.type || '',
        fileLastModified: draft?.file?.lastModified || 0,
    })));
};

export const usePosts = (currentUser, options = {}) => {
    const {
        autoFetch = true,
        createTimelineLoad,
        createPostRequest,
        getPostTitle,
        initialVisibility = 'Public',
        attachmentDraftVisibility = 'Only me',
        normalizePostVisibilityForUi = toUiVisibility,
        resetKey = null,
    } = options;
    const [posts, setPosts] = useState([]);
    const postsRef = useRef([]);
    const postMutationStateRef = useRef({});
    const pendingCreatePostRef = useRef(null);
    const timelineLoadRequestRef = useRef(0);
    const timelineLoadRef = useRef(null);
    const isMountedRef = useRef(true);
    const [isComposerOpen, setIsComposerOpen] = useState(false);
    const [composerMode, setComposerMode] = useState('create'); // 'create' | 'edit'
    const [postContent, setPostContent] = useState('');
    const [postVisibility, setPostVisibility] = useState(initialVisibility);
    const [editingPost, setEditingPost] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [editVisibility, setEditVisibility] = useState(initialVisibility);

    const [postAttachments, setPostAttachments] = useState([]);
    const [postAttachmentError, setPostAttachmentError] = useState(null);
    const [editAttachments, setEditAttachments] = useState([]);
    const [editAttachmentError, setEditAttachmentError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingPosts, setIsLoadingPosts] = useState(false);
    const [postsError, setPostsError] = useState(null);
    const [hasMorePosts, setHasMorePosts] = useState(false);
    const [isLoadingMorePosts, setIsLoadingMorePosts] = useState(false);
    const resetKeyRef = useRef(resetKey);

    const syncPosts = useCallback((nextValue) => {
        const nextPosts = typeof nextValue === 'function'
            ? nextValue(postsRef.current)
            : nextValue;
        postsRef.current = nextPosts;
        setPosts(nextPosts);
        return nextPosts;
    }, []);

    const resetTimelineLoader = useCallback(() => {
        timelineLoadRef.current = null;
        setHasMorePosts(false);
        setIsLoadingMorePosts(false);
    }, []);

    const buildSeededTimelineRefreshOptions = useCallback(() => ({
        includeEntityItems: false,
        requirePostSource: true,
        seedItems: () => (
            Array.isArray(postsRef.current)
                ? postsRef.current.filter(isStandardPostTimelineItem)
                : []
        ),
        seedPostMutationState: () => postMutationStateRef.current,
    }), []);

    const loadTimeline = useCallback((timelineOptions) => {
        if (typeof createTimelineLoad === 'function') {
            return createTimelineLoad(timelineOptions);
        }
        return timelineService.createCurrentUserTimelineLoad(timelineOptions);
    }, [createTimelineLoad]);

    const submitCreatePost = useCallback((postData) => {
        if (typeof createPostRequest === 'function') {
            return createPostRequest(postData);
        }
        return postsService.createPost(postData);
    }, [createPostRequest]);

    const resolvePostTitle = useCallback(() => {
        if (typeof getPostTitle === 'function') {
            const value = getPostTitle();
            if (typeof value === 'string' && value.trim()) {
                return value.trim();
            }
        }
        return currentUser?.username || 'Unknown';
    }, [currentUser?.username, getPostTitle]);

    const fetchPosts = useCallback(async (options = {}) => {
        const requestId = timelineLoadRequestRef.current + 1;
        timelineLoadRequestRef.current = requestId;
        resetTimelineLoader();
        if (isMountedRef.current) {
            setPostsError(null);
            setIsLoadingPosts(true);
        }
        try {
            const timelineLoad = await loadTimeline({
                ...options,
                includeEntityItems: false,
                requirePostSource: true,
            });
            if (isMountedRef.current && timelineLoadRequestRef.current === requestId) {
                timelineLoadRef.current = timelineLoad;
                if (timelineLoad.nextSeedPostMutationState !== undefined) {
                    postMutationStateRef.current = timelineLoad.nextSeedPostMutationState;
                }
                syncPosts(timelineLoad.items);
                setHasMorePosts(timelineLoad.hasMore === true);
                setIsLoadingMorePosts(false);
                setPostsError(null);
            }
            return timelineLoad.items;
        } catch (err) {
            console.error('Error fetching posts:', err);
            if (isMountedRef.current && timelineLoadRequestRef.current === requestId) {
                setPostsError(getSafeErrorMessage(err, POSTS_LOAD_ERROR_MESSAGE));
                setHasMorePosts(false);
                setIsLoadingMorePosts(false);
            }
            throw err;
        } finally {
            if (isMountedRef.current && timelineLoadRequestRef.current === requestId) {
                setIsLoadingPosts(false);
            }
        }
    }, [loadTimeline, resetTimelineLoader, syncPosts]);

    const retryFetchPosts = useCallback(() => (
        fetchPosts().catch(() => {})
    ), [fetchPosts]);

    const loadMorePosts = useCallback(async () => {
        const timelineLoad = timelineLoadRef.current;
        if (
            !timelineLoad
            || typeof timelineLoad.loadNextPage !== 'function'
            || isLoadingMorePosts
            || !hasMorePosts
        ) {
            return postsRef.current;
        }

        const requestId = timelineLoadRequestRef.current;
        setIsLoadingMorePosts(true);
        try {
            await timelineLoad.loadNextPage((applyUpdate) => {
                if (isMountedRef.current && timelineLoadRequestRef.current === requestId) {
                    return syncPosts(applyUpdate);
                }
                return postsRef.current;
            });
            if (isMountedRef.current && timelineLoadRequestRef.current === requestId) {
                syncPosts(timelineLoad.items);
                setHasMorePosts(timelineLoad.hasMore === true);
            }
            return timelineLoad.items;
        } catch (err) {
            console.error('Error loading more timeline items:', err);
            throw err;
        } finally {
            if (isMountedRef.current && timelineLoadRequestRef.current === requestId) {
                setIsLoadingMorePosts(false);
            }
        }
    }, [hasMorePosts, isLoadingMorePosts, syncPosts]);

    const closeComposer = useCallback(() => {
        pendingCreatePostRef.current = null;
        cleanupDrafts(postAttachments);
        cleanupDrafts(editAttachments);
        setPostAttachments([]);
        setEditAttachments([]);
        setPostAttachmentError(null);
        setEditAttachmentError(null);
        setIsSubmitting(false);
        setIsComposerOpen(false);
        setComposerMode('create');
        setEditingPost(null);
        setPostContent('');
        setPostVisibility(initialVisibility);
        setEditContent('');
        setEditVisibility(initialVisibility);
    }, [postAttachments, editAttachments, initialVisibility]);

    const openCreateComposer = useCallback(() => {
        pendingCreatePostRef.current = null;
        setComposerMode('create');
        setEditingPost(null);
        setPostContent('');
        setPostVisibility(initialVisibility);
        setEditContent('');
        setEditVisibility(initialVisibility);
        setPostAttachments([]);
        setPostAttachmentError(null);
        setIsComposerOpen(true);
    }, [initialVisibility]);

    const startEditingPost = useCallback((post) => {
        if (!post) {
            return;
        }
        pendingCreatePostRef.current = null;
        cleanupDrafts(editAttachments);
        setComposerMode('edit');
        setEditingPost(post);
        setEditContent(normalizePostBody(post.body));
        setEditVisibility(normalizePostVisibilityForUi(post.visibility || post.visibilityLabel));
        const existingAttachments = Array.isArray(post.attachments)
            ? post.attachments.map(createDraftFromAttachment)
            : [];
        setEditAttachments(existingAttachments);
        setEditAttachmentError(null);
        setIsComposerOpen(true);
    }, [editAttachments, normalizePostVisibilityForUi]);

    const addAttachments = useCallback((files, mode) => {
        const fileArray = Array.from(files || []);
        if (!fileArray.length) {
            return;
        }
        const setDrafts = mode === 'edit' ? setEditAttachments : setPostAttachments;
        const setError = mode === 'edit' ? setEditAttachmentError : setPostAttachmentError;

        setDrafts((prev) => {
            let remainingSlots = MAX_POST_ATTACHMENTS - prev.length;
            const accepted = [];
            let errorMessage = '';

            for (const file of fileArray) {
                if (remainingSlots <= 0) {
                    errorMessage = `Only ${MAX_POST_ATTACHMENTS} attachments allowed per post.`;
                    break;
                }
                if (!isSupportedType(file)) {
                    errorMessage = 'Unsupported file type.';
                    continue;
                }
                if (file.size > MAX_POST_ATTACHMENT_SIZE_MB * 1024 * 1024) {
                    errorMessage = `Files must be ≤ ${MAX_POST_ATTACHMENT_SIZE_MB}MB.`;
                    continue;
                }
                accepted.push(createDraftFromFile(file));
                remainingSlots -= 1;
            }

            if (errorMessage) {
                setError(errorMessage);
            } else {
                setError(null);
            }

            if (!accepted.length) {
                return prev;
            }
            return [...prev, ...accepted];
        });
    }, []);

    const removeAttachment = useCallback((attachmentId, mode) => {
        if (!attachmentId) {
            return;
        }
        const setDrafts = mode === 'edit' ? setEditAttachments : setPostAttachments;
        const setError = mode === 'edit' ? setEditAttachmentError : setPostAttachmentError;

        setDrafts((prev) => {
            const index = prev.findIndex((draft) => draft.id === attachmentId);
            if (index === -1) {
                return prev;
            }
            const draft = prev[index];
            revokePreviewUrl(draft);
            const next = [...prev.slice(0, index), ...prev.slice(index + 1)];
            if (next.length < MAX_POST_ATTACHMENTS) {
                setError(null);
            }
            return next;
        });
    }, []);

    const prepareAttachmentPayload = useCallback(async (postId, drafts, setError) => {
        if (!Array.isArray(drafts) || drafts.length === 0) {
            setError(null);
            return [];
        }
        const attachments = [];
        for (const draft of drafts) {
            if (draft.source === 'remote' && draft.metadata) {
                attachments.push(draft.metadata);
                continue;
            }
            if (draft.source === 'local' && draft.file) {
                try {
                    const response = await uploadPostAttachment(postId, draft.file);
                    attachments.push(response);
                } catch (error) {
                    const message = getSafeErrorMessage(error, 'Failed to upload attachment');
                    setError(message);
                    throw error;
                }
            }
        }
        setError(null);
        return attachments;
    }, []);

    const addCreateAttachments = useCallback((files) => addAttachments(files, 'create'), [addAttachments]);
    const removeCreateAttachment = useCallback((id) => removeAttachment(id, 'create'), [removeAttachment]);
    const addEditAttachments = useCallback((files) => addAttachments(files, 'edit'), [addAttachments]);
    const removeEditAttachment = useCallback((id) => removeAttachment(id, 'edit'), [removeAttachment]);

    const createPost = useCallback(async () => {
        if (isSubmitting) return;
        const trimmedBody = (postContent || '').trim();
        const hasBody = trimmedBody.length > 0;
        const hasAttachments = Array.isArray(postAttachments) && postAttachments.length > 0;
        const attachmentsOnly = hasAttachments && !hasBody;
        const pendingCreatePost = pendingCreatePostRef.current;
        const shouldResumePendingCreate = Boolean(pendingCreatePost?.postId);
        const attachmentSignature = getAttachmentDraftSignature(postAttachments);
        // Keep attachment posts private until attachments are finalized to avoid orphaned public posts.
        const createVisibility = hasAttachments ? attachmentDraftVisibility : postVisibility;

        if (!hasBody && !hasAttachments) {
            setPostAttachmentError('Post must include text or media.');
            return;
        }

        let createdPost = null;
        let failureStage = null;
        let attemptAttachmentError = null;
        const setAttemptAttachmentError = (message) => {
            const nextMessage = typeof message === 'string' && message.trim() ? message.trim() : null;
            attemptAttachmentError = nextMessage;
            setPostAttachmentError(nextMessage);
        };
        try {
            setIsSubmitting(true);
            setAttemptAttachmentError(null);

            if (shouldResumePendingCreate) {
                createdPost = pendingCreatePost.createdPost || { id: pendingCreatePost.postId };
            } else {
                const createBody = hasBody ? trimmedBody : (
                    attachmentsOnly ? ATTACHMENTS_ONLY_BODY_PLACEHOLDER : ''
                );
                failureStage = 'create';
                createdPost = await submitCreatePost({
                    title: resolvePostTitle(),
                    body: createBody,
                    visibility: createVisibility,
                });
            }

            if (hasAttachments || shouldResumePendingCreate) {
                const targetId = createdPost?.id;
                if (!targetId) {
                    throw new Error('Post creation did not return an id.');
                }
                pendingCreatePostRef.current = {
                    postId: targetId,
                    createdPost,
                    failureStage: 'upload',
                    attachmentSignature,
                    attachmentsPayload: null,
                };

                let attachmentsPayload = [];
                const canReuseUploadedAttachments = hasAttachments
                    && pendingCreatePost?.failureStage === 'update'
                    && pendingCreatePost.attachmentSignature === attachmentSignature
                    && Array.isArray(pendingCreatePost.attachmentsPayload);
                if (canReuseUploadedAttachments) {
                    attachmentsPayload = pendingCreatePost.attachmentsPayload;
                } else if (hasAttachments) {
                    failureStage = 'upload';
                    attachmentsPayload = await prepareAttachmentPayload(
                        targetId,
                        postAttachments,
                        setAttemptAttachmentError
                    );
                }

                pendingCreatePostRef.current = {
                    postId: targetId,
                    createdPost,
                    failureStage: 'update',
                    attachmentSignature,
                    attachmentsPayload,
                };

                const backendAllowsEmptyBodyWithAttachments = true;
                const safeEmptyBody = resolveAttachmentsOnlyBody(backendAllowsEmptyBodyWithAttachments);
                const finalBody = hasBody ? trimmedBody : safeEmptyBody;
                failureStage = 'update';
                await postsService.updatePost(targetId, {
                    title: resolvePostTitle(),
                    body: finalBody,
                    visibility: postVisibility,
                    attachments: attachmentsPayload,
                });
            }
            pendingCreatePostRef.current = null;
            failureStage = 'refresh';
            await fetchPosts(buildSeededTimelineRefreshOptions());
            closeComposer();
        } catch (err) {
            console.error('Error posting:', err);
            const status = resolveErrorStatus(err);
            const draftRisk = hasAttachments
                && createdPost?.id
                && isRecoverableCreateFailureStage(failureStage);
            const shouldAttemptDelete = draftRisk
                && (failureStage === 'upload' || (failureStage === 'update' && isClientErrorStatus(status)));
            let shouldWarnDraft = draftRisk;

            if (shouldAttemptDelete) {
                try {
                    await postsService.deletePost(createdPost.id);
                    shouldWarnDraft = false;
                    pendingCreatePostRef.current = null;
                } catch (cleanupError) {
                    console.error('Error cleaning up failed post create:', cleanupError);
                    shouldWarnDraft = true;
                }
            }

            if (!shouldWarnDraft && !shouldResumePendingCreate) {
                pendingCreatePostRef.current = null;
            }

            const baseMessage = attemptAttachmentError || getSafeErrorMessage(err, 'Failed to create post');
            const finalMessage = shouldWarnDraft ? appendDraftRecoveryMessage(baseMessage) : baseMessage;
            setPostAttachmentError(finalMessage);
            setIsComposerOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    }, [isSubmitting, postContent, postAttachments, attachmentDraftVisibility, submitCreatePost, resolvePostTitle, postVisibility, prepareAttachmentPayload, fetchPosts, closeComposer, buildSeededTimelineRefreshOptions]);

    const updatePost = useCallback(async (postId) => {
        if (!editingPost || isSubmitting) return;
        let attemptAttachmentError = null;
        const setAttemptAttachmentError = (message) => {
            const nextMessage = typeof message === 'string' && message.trim() ? message.trim() : null;
            attemptAttachmentError = nextMessage;
            setEditAttachmentError(nextMessage);
        };
        try {
            setIsSubmitting(true);
            setAttemptAttachmentError(null);
            const attachmentsPayload = await prepareAttachmentPayload(
                postId,
                editAttachments,
                setAttemptAttachmentError
            );
            await postsService.updatePost(postId, {
                ...editingPost,
                body: editContent,
                visibility: editVisibility,
                attachments: attachmentsPayload,
            });
            await fetchPosts(buildSeededTimelineRefreshOptions());
            closeComposer();
        } catch (err) {
            console.error('Error updating post:', err);
            if (!attemptAttachmentError) {
                setEditAttachmentError(getSafeErrorMessage(err, 'Failed to update post'));
            }
            setIsComposerOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    }, [editingPost, isSubmitting, prepareAttachmentPayload, editAttachments, editContent, editVisibility, fetchPosts, closeComposer, buildSeededTimelineRefreshOptions]);

    const deletePost = useCallback(async (postId) => {
        if (!postId) {
            return;
        }

        const targetId = String(postId);
        const currentPosts = Array.isArray(postsRef.current) ? postsRef.current : [];
        const snapshotIndex = currentPosts.findIndex((post) => String(post?.id) === targetId);
        const removedPost = snapshotIndex >= 0 ? currentPosts[snapshotIndex] : null;
        const removedIndex = snapshotIndex;

        syncPosts((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) {
                return prev;
            }

            const index = prev.findIndex((post) => String(post?.id) === targetId);
            if (index === -1) {
                return prev;
            }

            return [...prev.slice(0, index), ...prev.slice(index + 1)];
        });

        try {
            await postsService.deletePost(postId);
            postMutationStateRef.current = removeTrackedPostMutationFields(
                postMutationStateRef.current,
                targetId,
            );
        } catch (err) {
            console.error('Error deleting post:', err);
            if (removedPost) {
                syncPosts((prev) => {
                    const next = Array.isArray(prev) ? prev.slice() : [];
                    const insertIndex = Math.min(Math.max(removedIndex, 0), next.length);
                    next.splice(insertIndex, 0, removedPost);
                    return next;
                });
            }
            throw err;
        }

        if (removedPost) {
            try {
                await fetchPosts(buildSeededTimelineRefreshOptions());
            } catch (refreshError) {
                console.error('Error refreshing posts after deletion:', refreshError);
            }
        }
    }, [fetchPosts, buildSeededTimelineRefreshOptions, syncPosts]);

    const updatePostMeta = useCallback((postId, partial) => {
        postMutationStateRef.current = addTrackedPostMutationFields(
            postMutationStateRef.current,
            postId,
            partial,
        );
        syncPosts((prev) => prev.map((post) => (
            post.id === postId ? { ...post, ...partial } : post
        )));
    }, [syncPosts]);

    const removeTimelineItem = useCallback(async (itemId) => {
        if (!itemId) {
            return;
        }
        const targetId = String(itemId);
        syncPosts((previousItems) => (
            Array.isArray(previousItems)
                ? previousItems.filter((item) => String(item?.id) !== targetId)
                : []
        ));

        try {
            await fetchPosts(buildSeededTimelineRefreshOptions());
        } catch (refreshError) {
            console.error('Error refreshing timeline after entity deletion:', refreshError);
        }
    }, [fetchPosts, syncPosts, buildSeededTimelineRefreshOptions]);

    useEffect(() => {
        if (resetKeyRef.current === resetKey) {
            return;
        }
        resetKeyRef.current = resetKey;
        cleanupDrafts(postAttachments);
        cleanupDrafts(editAttachments);
        postMutationStateRef.current = {};
        pendingCreatePostRef.current = null;
        timelineLoadRequestRef.current += 1;
        timelineLoadRef.current = null;
        syncPosts([]);
        resetTimelineLoader();
        setIsComposerOpen(false);
        setComposerMode('create');
        setEditingPost(null);
        setPostContent('');
        setPostVisibility(initialVisibility);
        setEditContent('');
        setEditVisibility(initialVisibility);
        setPostAttachments([]);
        setEditAttachments([]);
        setPostAttachmentError(null);
        setEditAttachmentError(null);
        setIsSubmitting(false);
        setIsLoadingPosts(false);
        setPostsError(null);
    }, [editAttachments, initialVisibility, postAttachments, resetKey, resetTimelineLoader, syncPosts]);

    useEffect(() => {
        if (!autoFetch) {
            timelineLoadRequestRef.current += 1;
            timelineLoadRef.current = null;
            postMutationStateRef.current = {};
            pendingCreatePostRef.current = null;
            syncPosts([]);
            resetTimelineLoader();
            setIsLoadingPosts(false);
            setPostsError(null);
            return;
        }
        retryFetchPosts();
    }, [autoFetch, resetTimelineLoader, retryFetchPosts, syncPosts]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            pendingCreatePostRef.current = null;
            timelineLoadRequestRef.current += 1;
            timelineLoadRef.current = null;
        };
    }, []);

    const deriveEditVisibility = useCallback(
        (visibility) => normalizePostVisibilityForUi(visibility),
        [normalizePostVisibilityForUi],
    );

    return {
        posts,
        isComposerOpen,
        setIsComposerOpen,
        composerMode,
        openCreateComposer,
        startEditingPost,
        closeComposer,
        postContent,
        setPostContent,
        postVisibility,
        setPostVisibility,
        editingPost,
        setEditingPost,
        editContent,
        setEditContent,
        editVisibility,
        setEditVisibility,
        createPost,
        updatePost,
        deletePost,
        fetchPosts,
        loadMorePosts,
        hasMorePosts,
        isLoadingPosts,
        isLoadingMorePosts,
        postsError,
        retryFetchPosts,
        deriveEditVisibility,
        updatePostMeta,
        removeTimelineItem,
        postAttachments,
        editAttachments,
        addCreateAttachments,
        removeCreateAttachment,
        addEditAttachments,
        removeEditAttachment,
        postAttachmentError,
        editAttachmentError,
        isSubmitting,
        maxAttachments: MAX_POST_ATTACHMENTS,
        maxAttachmentSizeMb: MAX_POST_ATTACHMENT_SIZE_MB,
    };
};
